import { useState } from "react";
import { TypedFilter } from "odata-query";
import { subscriptionService } from "@miplatform/api";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, IUserListItemResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

export const useUsers = ({ debouncedSearch, module }: IUseUsersProps = {}) => {
  const getUsersUUID = createUUID();
  const getUsersByWorkPermissionsUUID = createUUID();
  const getRecentUsersUUID = createUUID();
  const resendInvitationUUID = createUUID();

  const stateManager = useComponentState();

  const [users, setUsers] = useState<IUserListItemResponse[]>([]);
  const [recentUsers, setRecentUsers] = useState<IUserListItemResponse[]>([]);
  const [filter, setFilter] = useState<TypedFilter<IUserListItemResponse>>({});

  const fetchUsers = ({
    top,
    skip,
    sort,
    filters = {},
    params,
    query,
    signal,
    uuid,
  }: IApiRequest & { uuid: string }) => {
    stateManager.abort(uuid);

    const searchFilter = {
      or: {
        displayName: { contains: debouncedSearch },
        firstName: { contains: debouncedSearch },
        lastName: { contains: debouncedSearch },
      },
    };

    const combinedFilters = {
      ...filter,
      ...filters,
      ...(debouncedSearch && { and: [filter, filters, searchFilter] }),
    };

    if (module) {
      return stateManager.run(async () => {
        return subscriptionService.users.listByPermission({
          ...params,
          query,
          permission: module,
          sort: sort ?? "firstName",
          top,
          skip,
          filters: combinedFilters,
          signal,
        });
      }, uuid);
    }

    return stateManager.run(async () => {
      return subscriptionService.users.getAll({
        ...params,
        query,
        sort: sort ?? "firstName",
        top,
        skip,
        filters: combinedFilters,
        signal,
      });
    }, uuid);
  };

  const getUsers = (request: IApiRequest = {}) => {
    return fetchUsers({
      ...request,
      uuid: getUsersUUID,
    });
  };

  const getUsersIncludeDisabled = (request: IApiRequest = {}) => {
    return fetchUsers({
      ...request,
      uuid: getUsersUUID,
      query: { ...request.query, includeDisabled: true },
    });
  };

  const getRecentUsers = (request: IApiRequest = {}) => {
    return fetchUsers({
      ...request,
      uuid: getRecentUsersUUID,
    });
  };

  const getRecentUsersIncludeDisabled = (request: IApiRequest = {}) => {
    return fetchUsers({
      ...request,
      uuid: getRecentUsersUUID,
      query: { ...request.query, includeDisabled: true },
    });
  };

  const getUsersByWorkPermissions = ({
    top,
    skip,
    sort,
    filters = {},
    params,
    signal,
    query,
  }: IApiRequest = {}) => {
    stateManager.abort(getUsersByWorkPermissionsUUID);

    const searchFilter = {
      or: {
        displayName: { contains: debouncedSearch },
        firstName: { contains: debouncedSearch },
        lastName: { contains: debouncedSearch },
      },
    };

    const combinedFilters = {
      ...filter,
      ...filters,
      ...(debouncedSearch && { and: [filter, filters, searchFilter] }),
    };

    return stateManager.run(async () => {
      return subscriptionService.users.getByWorkPermissions({
        sort: sort ?? "firstName",
        top,
        skip,
        params,
        filters: combinedFilters,
        signal,
        query,
      });
    }, getUsersByWorkPermissionsUUID);
  };

  const resendInvitation = (user: IUserListItemResponse) => {
    const { id, email } = user;
    stateManager.abort(resendInvitationUUID);
    stateManager.run(() => {
      return subscriptionService.users.resendInvitation({
        id,
        toastMessageConfig: {
          success: {
            text: `Invitation resent successfully to ${email}.`,
          },
          failure: {
            text: "Failed to resend invitation. Please try again later.",
          },
        },
      });
    });
  };

  return {
    getUsers,
    getUsersIncludeDisabled,
    setUsers,
    users,
    recentUsers,
    setRecentUsers,
    filter,
    setFilter,
    loading: stateManager.isBusy(),
    resendInvitation,
    getRecentUsers,
    getRecentUsersIncludeDisabled,
    getUsersByWorkPermissions,
  };
};

interface IUseUsersProps {
  debouncedSearch?: string;
  module?: string;
}
